import React, { useState, forwardRef, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import './RightSidebar.css';
import './Sidebar.css';
import { supabase } from '../supabase';
import { database } from '../supabase';
import { SHOW_QUIZ_FEATURES } from '../config/features';
import ProCompareModal from './ProCompareModal';
import { 
    ChevronLeft, 
    ChevronRight, 
    MessageSquare, 
    BookOpen, 
    FileText, 
    Settings, 
    Star,
    Clock,
    TrendingUp,
    Users,
    Calendar,
    Bell,
    Link,
    ExternalLink,
    Search,
    Folder,
    Infinity
} from 'lucide-react';

const extractYoutubeVideoId = (url) => {
    if (!url || typeof url !== 'string') return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2] && match[2].length === 11 ? match[2] : null;
};

const getActivityIcon = (type) => {
    switch (type) {
        case 'youtube_video':
            return <Link size={16} />;
        case 'document':
            return <FileText size={16} />;
        case 'chat':
            return <MessageSquare size={16} />;
        case 'quiz':
            return <BookOpen size={16} />;
        default:
            return <FileText size={16} />;
    }
};

const ActivityLeadVisual = ({ activity }) => {
    const [imgFailed, setImgFailed] = useState(false);
    const videoId = extractYoutubeVideoId(activity.url);
    const showThumb = Boolean(videoId) && !imgFailed;

    return (
        <div className={`activity-icon ${showThumb ? 'activity-icon--video' : ''}`}>
            {showThumb ? (
                <img
                    src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                    alt=""
                    loading="lazy"
                    className="activity-thumb-img"
                    onError={() => setImgFailed(true)}
                />
            ) : (
                getActivityIcon(activity.type)
            )}
        </div>
    );
};

const RightSidebar = forwardRef(({ 
    isCollapsed, 
    onCollapse, 
    isMobileOpen, 
    onMobileClose,
    user,
    documents = [],
    currentDocumentId,
    onToggleFlashcards,
    onToggleQuiz,
    onToggleChat
}, ref) => {
    const [activeSection, setActiveSection] = useState('recent');
    const [recentActivity, setRecentActivity] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchModal, setShowSearchModal] = useState(false);
    const [filteredDocuments, setFilteredDocuments] = useState([]);
    const [showProCompareModal, setShowProCompareModal] = useState(false);

    const toggleCollapse = () => {
        onCollapse(!isCollapsed);
    };

    const closeMobileSidebar = () => {
        onMobileClose();
    };

    // Fetch recent activity from Supabase
    const fetchRecentActivity = async () => {
        if (!user?.uid) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            
            // Get Supabase user ID from Firebase UID
            const supabaseUser = await database.getUserByFirebaseUid(user.uid);
            if (!supabaseUser) {
                console.log('User not found in Supabase');
                setRecentActivity([]);
                setLoading(false);
                return;
            }
            
            // Fetch recent notes (web links, documents, etc.)
            const { data: notes, error: notesError } = await supabase
                .from('notes')
                .select('*')
                .eq('user_id', supabaseUser.id)
                .order('created_at', { ascending: false })
                .limit(5);

            if (notesError) {
                console.error('Error fetching notes:', notesError);
                setRecentActivity([]);
            } else {
                console.log('Fetched notes:', notes);
                const noteRows = (notes || []).filter(
                    (n) => SHOW_QUIZ_FEATURES || (n.type || '') !== 'quiz'
                );
                const activityItems = noteRows.map(note => ({
                    id: note.id,
                    type: note.type || 'document',
                    title: note.title || 'Untitled',
                    url: note.url,
                    createdAt: note.created_at,
                    summary: note.summary
                }));

                setRecentActivity(activityItems);
            }
        } catch (error) {
            console.error('Error fetching recent activity:', error);
            setRecentActivity([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch data when component mounts or user changes
    useEffect(() => {
        fetchRecentActivity();
    }, [user?.uid]);

    // Expose refresh function for debugging
    useEffect(() => {
        window.refreshRightSidebar = fetchRecentActivity;
        return () => {
            delete window.refreshRightSidebar;
        };
    }, []);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleSearchClick = () => {
        setShowSearchModal(true);
    };

    const handleCloseSearchModal = () => {
        setShowSearchModal(false);
        setSearchQuery('');
        setFilteredDocuments([]);
    };

    useEffect(() => {
        if (searchQuery.trim()) {
            const filtered = documents.filter(doc =>
                (doc.document_name || doc.name || doc.title || '').toLowerCase().includes(searchQuery.toLowerCase())
            );
            setFilteredDocuments(filtered);
        } else {
            setFilteredDocuments([]);
        }
    }, [searchQuery, documents]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && showSearchModal) {
                handleCloseSearchModal();
            }
        };

        if (showSearchModal) {
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [showSearchModal]);

    // Format time ago
    const getTimeAgo = (dateString) => {
        const now = new Date();
        const date = new Date(dateString);
        const diffInMinutes = Math.floor((now - date) / (1000 * 60));
        
        if (diffInMinutes < 1) return 'Just now';
        if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
        if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
        return `${Math.floor(diffInMinutes / 1440)}d ago`;
    };

    const sidebarContent = (
        <div className={`right-sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}>
            {/* Header */}
            <div className="right-sidebar-header">
                <div className="right-sidebar-title">
                    <RouterLink
                        to={user?.uid ? `/main/${user.uid}` : '/'}
                        className="right-sidebar-brand-link"
                        title="Study Space — home"
                        aria-label="Study Space — go to home"
                        onClick={() => {
                            if (typeof onMobileClose === 'function') onMobileClose();
                        }}
                    >
                        <BookOpen className="right-sidebar-brand-icon" size={20} aria-hidden />
                        {!isCollapsed && (
                            <span className="right-sidebar-brand-text">Study Space</span>
                        )}
                    </RouterLink>
                </div>
                <button 
                    className="right-sidebar-toggle"
                    onClick={toggleCollapse}
                    title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                    {isCollapsed ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
                </button>
            </div>

            {!isCollapsed && (
                <div className="right-sidebar-search">
                    <div className="search-container" onClick={handleSearchClick}>
                        <input
                            type="text"
                            placeholder="Search"
                            className="search-input"
                            value={searchQuery}
                            readOnly
                        />
                        <button
                            type="button"
                            className="search-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSearchClick();
                            }}
                        >
                            <Search className="search-icon" size={16} />
                        </button>
                    </div>
                </div>
            )}

            {/* Content */}
            <div className="right-sidebar-content">
                {/* Recent Activity */}
                <div className="right-sidebar-section">
                    <div className="section-header">
                        {!isCollapsed && <h4>Recent Notes</h4>}
                        {isCollapsed && <Clock size={20} className="section-icon" />}
                    </div>
                    <div className="section-content">
                        {!isCollapsed && (
                            <div className="activity-list">
                                {loading ? (
                                    <div className="activity-loading">
                                        <div className="loading-dots">
                                            <div className="loading-dot"></div>
                                            <div className="loading-dot"></div>
                                            <div className="loading-dot"></div>
                                        </div>
                                        <span>Loading...</span>
                                    </div>
                                ) : recentActivity.length > 0 ? (
                                    recentActivity.map((activity) => (
                                        <RouterLink
                                            key={activity.id}
                                            to={`/notes/${user?.uid}/${activity.id}`}
                                            className="new-activity-link"
                                            title="Open note"
                                        >
                                            <div className="activity-item">
                                                <ActivityLeadVisual activity={activity} />
                                                <div className="activity-text">
                                                    <span className="activity-title" title={activity.title}>
                                                        {activity.title.length > 25
                                                            ? `${activity.title.substring(0, 25)}...`
                                                            : activity.title}
                                                    </span>
                                                    <span className="activity-time">
                                                        {getTimeAgo(activity.createdAt)}
                                                    </span>
                                                </div>
                                                <ExternalLink size={12} className="activity-external-icon" aria-hidden />
                                            </div>
                                            <span className="activity-view-note">View note</span>
                                        </RouterLink>
                                    ))
                                ) : (
                                    <div className="no-activity">
                                        <div className="no-activity-icon">
                                            <Clock size={20} />
                                        </div>
                                        <span className="no-activity-text">No activity yet</span>
                                        <span className="no-activity-subtext">Import web links to see them here</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="right-sidebar-section">
                    <div className="section-header">
                        {!isCollapsed && <h4 style={{marginBottom:'15px'}}>Quick Actions</h4>}
                        {isCollapsed && <Star size={20} className="section-icon" />}
                    </div>
                    <div className="section-content">
                        {!isCollapsed && (
                            <div className="quick-actions">
                                {currentDocumentId ? (
                                    <>
                                        <button
                                            type="button"
                                            className="quick-action-btn"
                                            onClick={onToggleChat}
                                            title="Start new chat"
                                        >
                                            <MessageSquare size={16} />
                                            <span>New Chat</span>
                                        </button>
                                        {SHOW_QUIZ_FEATURES && (
                                        <button
                                            type="button"
                                            className="quick-action-btn"
                                            onClick={onToggleQuiz}
                                            title="Start quiz"
                                        >
                                            <BookOpen size={16} />
                                            <span>Start Quiz</span>
                                        </button>
                                        )}
                                        <button
                                            type="button"
                                            className="quick-action-btn"
                                            onClick={onToggleFlashcards}
                                            title="View flashcards"
                                        >
                                            <FileText size={16} />
                                            <span>Flashcards</span>
                                        </button>
                                    </>
                                ) : null}
                                <RouterLink
                                    to="/study-plan"
                                    className="quick-action-btn quick-action-btn--nav"
                                    title="Open study plan"
                                >
                                    <TrendingUp size={16} />
                                    <span>Study Plan</span>
                                </RouterLink>
                            </div>
                        )}
                    </div>
                </div>

                {/* <div className="right-sidebar-section">
                    <div className="section-header">
                        {!isCollapsed && <h4 style={{marginBottom:'15px'}}>Recent</h4>}
                        {isCollapsed && <Star size={20} className="section-icon" />}
                    </div>
                </div> */}

            </div>

            <div
                className={`right-sidebar-upgrade-rail ${
                    isCollapsed ? 'right-sidebar-upgrade-rail--collapsed' : ''
                }`}
            >
                <button
                    type="button"
                    className="right-sidebar-unlimited-btn"
                    onClick={() => setShowProCompareModal(true)}
                    title="Compare Free vs Pro"
                    aria-label="Unlimited everything — compare Free and Pro"
                >
                    <Infinity size={18} className="right-sidebar-unlimited-btn-icon" aria-hidden />
                    {!isCollapsed && <span>Unlimited everything</span>}
                </button>
            </div>
        </div>
    );

    return (
        <>
            {sidebarContent}
            <ProCompareModal
                isOpen={showProCompareModal}
                onClose={() => setShowProCompareModal(false)}
                user={user}
            />
            {showSearchModal && (
                <div className="search-modal-overlay" onClick={handleCloseSearchModal}>
                    <div className="search-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="search-modal-header">
                            <div className="search-input-container">
                                <Search size={20} className="search-modal-icon" />
                                <input
                                    type="text"
                                    placeholder="Search your notes, flashcards, and documents..."
                                    className="search-modal-input"
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    autoFocus
                                />
                            </div>
                        </div>

                        <div className="search-modal-content">
                            {!searchQuery.trim() && (
                                <div className="search-section">
                                    <h3 className="search-section-title">Quick Actions</h3>
                                    <div className="search-actions">
                                        <div className="search-action-item primary">
                                            <div className="search-action-icon">+</div>
                                            <span>Drag or drop PDF documents to create notes, flashcards, and more...</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="search-section">
                                <h3 className="search-section-title">
                                    {searchQuery.trim() ? 'Search Results' : 'Recent Items'}
                                </h3>
                                <div className="search-results">
                                    {searchQuery.trim() ? (
                                        filteredDocuments.length > 0 ? (
                                            filteredDocuments.map((document, index) => (
                                                <div key={document.document_id || document.document_name || index} className="search-result-item">
                                                    <div className="search-result-icon"><Folder className="search-result-icon-icon" /></div>
                                                    <div className="search-result-content">
                                                        <div className="search-result-title">{document.document_name || document.name || document.title || 'Untitled Document'}</div>
                                                        <div className="search-result-meta">
                                                            PDF • {document.upload_date ? new Date(document.upload_date).toLocaleDateString() : 'Recently uploaded'}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="search-result-item">
                                                <div className="search-result-icon"><Folder className="search-result-icon-icon" /></div>
                                                <div className="search-result-content">
                                                    <div className="search-result-title">No results found</div>
                                                    <div className="search-result-meta">Try a different search term</div>
                                                </div>
                                            </div>
                                        )
                                    ) : (
                                        documents.length > 0 ? (
                                            documents.slice(0, 5).map((document, index) => (
                                                <div key={document.document_id || document.document_name || index} className="search-result-item">
                                                    <div className="search-result-icon"><Folder className="search-result-icon-icon" /></div>
                                                    <div className="search-result-content">
                                                        <div className="search-result-title">{document.document_name || document.name || document.title || 'Untitled Document'}</div>
                                                        <div className="search-result-meta">
                                                            PDF • {document.upload_date ? new Date(document.upload_date).toLocaleDateString() : 'Recently uploaded'}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="search-result-item">
                                                <div className="search-result-icon"><Folder className="search-result-icon-icon" /></div>
                                                <div className="search-result-content">
                                                    <div className="search-result-title">No documents uploaded yet</div>
                                                    <div className="search-result-meta">Upload a PDF to get started</div>
                                                </div>
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="search-modal-footer">
                            <div className="search-shortcuts">
                                <div className="search-shortcut">
                                    <div className="search-shortcut-key">ESC</div>
                                    <span>Close</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Mobile Overlay */}
            {isMobileOpen && (
                <div className="right-sidebar-overlay" onClick={closeMobileSidebar}></div>
            )}
        </>
    );
});

RightSidebar.displayName = 'RightSidebar';

export default RightSidebar;
